import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'

export type TransitionPhase = 'language' | 'dissolving' | 'cookies' | 'hero' | 'complete'

interface AppReadyContextType {
  isReady: boolean
  setReady: () => void
  isLanguageSelected: boolean
  setLanguageSelected: (selected: boolean) => void
  hasScrolled: boolean
  setTransitionComplete: (complete: boolean) => void
  canShowChatPromo: boolean
  isCookiesAccepted: boolean
  setCookiesAccepted: (accepted: boolean) => void
  // Transition orchestration
  transitionPhase: TransitionPhase
  setTransitionPhase: (phase: TransitionPhase) => void
  starfieldVelocity: number
  setStarfieldVelocity: (v: number) => void
  // Title visibility for emergence animation
  titleVisible: boolean
  setTitleVisible: (visible: boolean) => void
}

const AppReadyContext = createContext<AppReadyContextType | undefined>(undefined)

export function AppReadyProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false)
  const [isLanguageSelected, setIsLanguageSelected] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [transitionComplete, setTransitionComplete] = useState(false)
  const [cookiesAccepted, setCookiesAccepted] = useState(false)
  // Transition orchestration state - initialize from localStorage to skip language gate if already selected
  const [transitionPhase, setTransitionPhaseState] = useState<TransitionPhase>(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('lukhas_language_selected') === 'true') {
      return 'complete'
    }
    return 'language'
  })
  const [starfieldVelocity, setStarfieldVelocityState] = useState(1)
  const [titleVisible, setTitleVisibleState] = useState(false)

  const setReady = useCallback(() => {
    setIsReady(true)
  }, [])

  const setLanguageSelected = useCallback((selected: boolean) => {
    setIsLanguageSelected(selected)
  }, [])

  const handleSetTransitionComplete = useCallback((complete: boolean) => {
    setTransitionComplete(complete)
  }, [])

  const handleSetCookiesAccepted = useCallback((accepted: boolean) => {
    setCookiesAccepted(accepted)
  }, [])

  const setTransitionPhase = useCallback((phase: TransitionPhase) => {
    setTransitionPhaseState(phase)
  }, [])

  const setStarfieldVelocity = useCallback((v: number) => {
    setStarfieldVelocityState(v)
  }, [])

  const setTitleVisible = useCallback((visible: boolean) => {
    setTitleVisibleState(visible)
  }, [])

  // Track scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100 && !hasScrolled) {
        setHasScrolled(true)
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [hasScrolled])

  // Chat promo can show when language selected, scrolled, transition complete, and cookies accepted
  const canShowChatPromo = isLanguageSelected && hasScrolled && transitionComplete && cookiesAccepted

  return (
    <AppReadyContext.Provider value={{
      isReady,
      setReady,
      isLanguageSelected,
      setLanguageSelected,
      hasScrolled,
      setTransitionComplete: handleSetTransitionComplete,
      canShowChatPromo,
      isCookiesAccepted: cookiesAccepted,
      setCookiesAccepted: handleSetCookiesAccepted,
      transitionPhase,
      setTransitionPhase,
      starfieldVelocity,
      setStarfieldVelocity,
      titleVisible,
      setTitleVisible,
    }}>
      {children}
    </AppReadyContext.Provider>
  )
}

export function useAppReady() {
  const context = useContext(AppReadyContext)
  if (!context) {
    // Return a default instead of throwing to avoid breaking the app
    return {
      isReady: true,
      setReady: () => {},
      isLanguageSelected: true,
      setLanguageSelected: () => {},
      hasScrolled: true,
      setTransitionComplete: () => {},
      canShowChatPromo: false,
      isCookiesAccepted: true,
      setCookiesAccepted: () => {},
      transitionPhase: 'complete' as TransitionPhase,
      setTransitionPhase: () => {},
      starfieldVelocity: 1,
      setStarfieldVelocity: () => {},
      titleVisible: true,
      setTitleVisible: () => {},
    }
  }
  return context
}
